import { Component, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import '../styles/invalid.css';

const ErrorBoundary = ({ children }) => {
  const currentUser = useSelector((state) => state.user);

  const [hasError, setHasError] = useState(false);
  const [errorInfo, setErrorInfo] = useState({
    message: '',
    stack: '',
    componentStack: '',
    type: '',
    eventDetails: {},
    timestamp: '',
  });

  const lastErrorRef = useRef({ message: '', stack: '' });
  const lastErrorTimestampRef = useRef(0);
  const errorLogDebounceTime = 1000;

  const isDev = process.env.NODE_ENV === 'development';

  const logErrorToServer = useCallback(
    (errorData) => {
      if (!currentUser?.id) {
        if (isDev) console.warn('No user ID for server logging:', errorData);
        return;
      }

      const payload = {
        user_id: currentUser.id,
        error_message: errorData.message || 'Unknown error',
        error_stack: errorData.stack || 'No stack trace',
        component_stack: errorData.componentStack || 'N/A',
        error_type: errorData.type || 'Unknown',
        timestamp: errorData.timestamp || new Date().toISOString(),
      };

      fetch('/api/LogError', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Server log failed: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          if (isDev) console.log('Error logged to server:', data);
        })
        .catch((err) => {
          if (isDev) console.error('Failed to log error to server:', err);
        });
    },
    [currentUser, isDev]
  );

  const handleError = useCallback(
    (errorData) => {
      try {
        const currentTime = Date.now();
        const { message, stack, type, componentStack, eventDetails } = errorData;
        const lastError = lastErrorRef.current;

        const isNewOrTimedOut =
          message !== lastError.message ||
          stack !== lastError.stack ||
          currentTime - lastErrorTimestampRef.current > errorLogDebounceTime;

        if (isNewOrTimedOut) {
          if (isDev) console.error(`ErrorBoundary handled (${type}):`, errorData);

          setHasError(true);
          setErrorInfo({
            message,
            stack,
            type,
            componentStack,
            eventDetails,
            timestamp: new Date().toISOString(),
          });

          lastErrorRef.current = { message, stack };
          lastErrorTimestampRef.current = currentTime;

          logErrorToServer(errorData);
        }
      } catch (metaErr) {
        if (isDev) console.error('Error in handleError:', metaErr);
      }
    },
    [logErrorToServer, isDev]
  );

  useEffect(() => {
    const shouldIgnoreError = (msg) =>
      msg?.includes('ResizeObserver loop completed') ||
      msg?.includes('ResizeObserver loop limit exceeded');

    const errorHandler = (event) => {
      try {
        if (shouldIgnoreError(event.message)) {
          if (isDev) console.warn('Ignored ResizeObserver warning:', event.message);
          return;
        }

        let error = event.error;
        if (!error) {
          error = new Error(event.message || 'Unknown Global Error');
          error.stack = event.error?.stack || 'No stack trace available';
        }

        const errorData = {
          message: error.message || error.toString() || 'Unknown global error',
          stack: error.stack || 'No stack trace',
          type: 'GlobalError',
          componentStack: 'N/A',
          timestamp: new Date().toISOString(),
          eventDetails: {
            filename: event.filename || 'N/A',
            lineno: event.lineno || 'N/A',
            colno: event.colno || 'N/A',
            message: event.message || 'N/A',
          },
        };

        if (isDev) console.error('Global error event:', errorData);
        handleError(errorData);
      } catch (err) {
        if (isDev) console.error('Error in errorHandler:', err);
      }
    };

    const unhandledRejectionHandler = (event) => {
      try {
        const reasonMessage =
          typeof event.reason === 'string'
            ? event.reason
            : event.reason?.message || event.reason?.toString() || '';

        if (shouldIgnoreError(reasonMessage)) {
          if (isDev) console.warn('Ignored ResizeObserver rejection:', reasonMessage);
          return;
        }

        event.preventDefault();
        let error = event.reason;
        if (!error) {
          error = new Error('Unknown Promise Rejection');
          error.stack = 'No stack trace';
        }

        const errorData = {
          message: error.message || error.toString() || 'Unknown promise rejection',
          stack: error.stack || 'No stack trace',
          type: 'UnhandledRejection',
          componentStack: 'N/A',
          timestamp: new Date().toISOString(),
          eventDetails: {
            reason:
              typeof event.reason === 'object'
                ? JSON.stringify(event.reason, null, 2)
                : event.reason?.toString() || 'N/A',
            promise: event.promise ? 'Promise object' : 'N/A',
          },
        };

        if (isDev) console.error('Unhandled rejection event:', errorData);
        handleError(errorData);
      } catch (err) {
        if (isDev) console.error('Error in unhandledRejectionHandler:', err);
      }
    };

    const asyncErrorHandler = (event) => {
      try {
        const err = event.detail?.error;
        if (err) {
          if (shouldIgnoreError(err.message)) {
            if (isDev) console.warn('Ignored ResizeObserver async error:', err.message);
            return;
          }

          const errorData = {
            message: err.message || 'Unknown async error',
            stack: err.stack || 'No stack trace',
            type: 'AsyncError',
            componentStack: 'N/A',
            timestamp: new Date().toISOString(),
            eventDetails: { detail: JSON.stringify(event.detail, null, 2) },
          };
          if (isDev) console.error('Async error event:', errorData);
          handleError(errorData);
        }
      } catch (err) {
        if (isDev) console.error('Error in asyncErrorHandler:', err);
      }
    };

    window.addEventListener('error', errorHandler);
    window.addEventListener('unhandledrejection', unhandledRejectionHandler);
    window.addEventListener('errorBoundaryAsync', asyncErrorHandler);

    return () => {
      window.removeEventListener('error', errorHandler);
      window.removeEventListener('unhandledrejection', unhandledRejectionHandler);
      window.removeEventListener('errorBoundaryAsync', asyncErrorHandler);
    };
  }, [handleError, isDev]);

  const dispatchAsyncError = useCallback((err) => {
    window.dispatchEvent(new CustomEvent('errorBoundaryAsync', { detail: { error: err } }));
  }, []);

  if (hasError) {
    return (
      <div id="invalid">
        <div className="invalid">
          <div className="invalid-code">
            <h1>Error</h1>
          </div>
          <h2>Something Went Wrong</h2>
          {isDev && (
            <div
              style={{
                maxHeight: '300px',
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              <p>
                <strong>Error ({errorInfo.type}):</strong> {errorInfo.message}
              </p>
              <p>
                <strong>Stack:</strong>
              </p>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{errorInfo.stack}</pre>
              {errorInfo.componentStack !== 'N/A' && (
                <>
                  <p>
                    <strong>Component Stack:</strong>
                  </p>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {errorInfo.componentStack}
                  </pre>
                </>
              )}
              {Object.keys(errorInfo.eventDetails).length > 0 && (
                <>
                  <p>
                    <strong>Event Details:</strong>
                  </p>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(errorInfo.eventDetails, null, 2)}
                  </pre>
                </>
              )}
              <p>
                <strong>Timestamp:</strong> {errorInfo.timestamp}
              </p>
            </div>
          )}
          <p>Please refresh the page or try again later.</p>
          <a href="/">Go to Homepage</a>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundaryClass
      handleError={handleError}
      logErrorToServer={logErrorToServer}
      isDev={isDev}
      dispatchAsyncError={dispatchAsyncError}
    >
      {children}
    </ErrorBoundaryClass>
  );
};

class ErrorBoundaryClass extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const { handleError, logErrorToServer, isDev } = this.props;

    if (
      error.message?.includes('ResizeObserver loop completed') ||
      error.message?.includes('ResizeObserver loop limit exceeded')
    ) {
      if (isDev) console.warn('Ignored ResizeObserver in componentDidCatch:', error.message);
      return;
    }

    const errorData = {
      message: error.message || error.toString() || 'Unknown React error',
      stack: error.stack || 'No stack trace',
      type: 'ReactBoundary',
      componentStack: errorInfo.componentStack || 'N/A',
      timestamp: new Date().toISOString(),
      eventDetails: {},
    };

    if (isDev) console.error('React componentDidCatch:', errorData);
    handleError(errorData);
    logErrorToServer(errorData);
  }

  render() {
    return this.props.children;
  }
}

export default ErrorBoundary;
